import * as React from "react";
import { useState } from "react";
import Image from "next/image";
import { NextRouter, useRouter } from "next/router";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import NotificationsIcon from "@mui/icons-material/Notifications";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import PeopleIcon from "@mui/icons-material/People";
import BoltIcon from "@mui/icons-material/Bolt";
import AddIcon from "@mui/icons-material/Add";
import EventIcon from "@mui/icons-material/Event";
import SearchIcon from "@mui/icons-material/Search";
import MailIcon from "@mui/icons-material/Mail";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import BookmarkIcon from "@mui/icons-material/Bookmark";

import CompanyLogo from "../../assets/images/leap.png";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { loadString, resetScrollForRoute } from "../../utils/storage";
import {
  storageKeys,
  pages,
  settings,
  appConfig,
  analyticsConstants,
} from "../../constants";
import { useUserLogoutMutation } from "../../services/authAPI";
import { logout } from "../../slices/authSlice";
import { CustomDialog } from "../../ui";
import Link from "../common/Link";
import { shareProfile } from "../../utils/share";
import ShareProfile from "./ShareProfile";
import { ScrollContext } from "../context/ScrollContext";
import { Badge, Paper, Stack } from "@mui/material";
import { useGetUnreadInAppNotificationsCountQuery } from "../../services/notificationAPI";
import { fetchChannelUnreadCount } from "../../slices/mainInboxSlice";
import { analyticsLogout, trackEvent } from "../../utils/analytics";
import { BookmarkOutlined } from "@mui/icons-material";
import { useBookmarkProfileMutation } from "../../services/profileAPI";

const {
  retainTabRoutesWithInfiniteScroll,
  experienceTabRoutes,
  learnTabRoutes,
} = appConfig;
const feedTabRoutes = retainTabRoutesWithInfiniteScroll;

function getHeaderRouteColor(
  label: string,
  router: NextRouter,
  route: string | undefined
): "primary" | "secondary" {
  // TODO: can club all the conditions with ||(just for sake of readibility have put if else if)
  if (label === "feed" && feedTabRoutes.includes(router.asPath)) {
    return "primary";
  } else if (
    label === "experiences" &&
    experienceTabRoutes.includes(router.asPath)
  ) {
    return "primary";
  } else if (label === "learn" && learnTabRoutes.includes(router.asPath)) {
    return "primary";
  } else if (route && router.asPath.includes(route)) {
    return "primary";
  }

  return "secondary";
}

const StyledCompanyLogo = styled(Image)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginRight: "100px",
  display: "none",
}));

const Dot = styled("div")({
  height: "4px",
  width: "4px",
  borderRadius: "2px",
  backgroundColor: "#f42b4d",
  marginLeft: "8px",
});

type MainHeaderPropsType = {
  showBookmarkIcon?: boolean;
  currentUserId?: string;
  profileId?: string;
  isBookmarked?: boolean;
};

const MainHeader = ({
  showBookmarkIcon = false,
  currentUserId = "",
  profileId = "",
  isBookmarked = false,
}: MainHeaderPropsType) => {
  const router = useRouter();
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const { resetScrollInfo } = React.useContext(ScrollContext);

  const [open, setOpen] = useState(false);
  const [profileLink, setProfileLink] = React.useState(null);

  const onOpen = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  // const user = useAppSelector((state) => state.auth);
  const [user, setUser] = useState({ name: "", userId: "", userImage: "" });
  const unreadChannelCount = useAppSelector(
    (state) => state.mainInbox.unreadChannelCount
  );
  const { data: unreadInAppNotificationsCount } =
    useGetUnreadInAppNotificationsCountQuery(undefined, {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    });

  const [userLogout] = useUserLogoutMutation();
  const [bookmarkProfile, { isLoading }] = useBookmarkProfileMutation();
  const dispatch = useAppDispatch();

  const handleOpenNavMenu = (event: any) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event: any) => {
    setAnchorElUser(event.currentTarget);
  };

  const onBookmarkProfilePressHandler = async () => {
    try {
      await bookmarkProfile(profileId).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  const handleCloseNavMenu = (route?: string, label?: string) => {
    if (label === "clubhouses") {
      trackEvent("click_bottombar", { bottombarvalue: label });
    } else {
      trackEvent("click_bottombar", { bottombarvalue: label });
    }
    resetScrollInfo();
    setAnchorElNav(null);
    route && resetScrollForRoute(route);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleUserMenuItemClick = async (id: string, label: string) => {
    resetScrollInfo();
    if (id === "visit_profile" && user?.userId) {
      trackEvent("sb_profile");
      router.push(`/profile/${user.userId}`);
      handleCloseUserMenu();
    } else if (id === "my_activity") {
      router.push(`/activity/${user.userId}`);
      handleCloseUserMenu();
    } else if (id === "invite_friend" && user?.userId) {
      trackEvent("invite_clickheader");
      router.push("/invite");
      handleCloseUserMenu();
    } else if (id === "referral" && user?.userId) {
      trackEvent("refer_and_win_button_click", {
        screen_section: "side_tab",
      });
      router.push("/referral");
      handleCloseUserMenu();
    } else if (id === "share_feedback" && user?.userId) {
      trackEvent("sb_sharefeedback");
      router.push("/feedback");
      handleCloseUserMenu();
    } else if (id === "share_profile" && user?.userId) {
      trackEvent("sb_sharemyprofile");
      const link = shareProfile(user?.userId, user?.name);
      setProfileLink(link);
      onOpen();
      handleCloseUserMenu();
    } else if (id === "change_password" && user?.userId) {
      router.push("/change-password");
      handleCloseUserMenu();
    }
    if (id === "logout") {
      try {
        router.replace("/login");
        setTimeout(async () => {
          const refreshToken = await loadString(storageKeys.USER_REFRESH_TOKEN);
          await userLogout(refreshToken!);
          sessionStorage.clear();
          dispatch(logout());
          analyticsLogout();
        }, 1000);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const getButtonIcon = (route = "") => {
    if (route === "/feed/all") {
      return <RssFeedIcon />;
    }
    if (route === "/joined-clubhouses/all") {
      return <PeopleIcon />;
    }
    if (route === "/superconnections") {
      return <AddIcon />;
    }
    if (route === "/experiences/upcoming") {
      return <EventIcon />;
    }
    if (route === "/learn/courses") {
      return (
        <i
          className="fas fa-chalkboard-teacher fa-xs"
          style={{ fontSize: "14px" }}
        ></i>
      );
    }

    return null;
  };

  React.useEffect(() => {
    dispatch(fetchChannelUnreadCount());
  }, []);

  React.useEffect(() => {
    const getUserDetailsFromLocalStorage = async () => {
      const userId = await loadString(storageKeys.USER_ID);
      const name = await loadString(storageKeys.USER_NAME);
      const userImage = await loadString(storageKeys.USER_PROFILE_IMAGE);
      setUser({
        name,
        userId,
        userImage,
      });
    };

    getUserDetailsFromLocalStorage();
  }, []);

  const getTabName = () => {
    if (router.pathname.includes("clubs")) {
      // return "clubhouse";
      return "clubs";
    }
    if (router.pathname.includes("feed")) {
      return "feed";
    }
    if (router.pathname.includes("learn")) {
      return "learn";
    }
    if (router.pathname.includes("superconnection")) {
      return "my_network";
    }
    if (router.pathname.includes("experience")) {
      return "experiences";
    }
    return "";
  };

  const navigateToSearchMembersPage = () => {
    router.push("/search");
  };

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          top: "0px",
          backgroundColor: "background.paper",
          boxShadow: "none",
          borderBottomWidth: "1px",
          borderBottomColor: "grey.300",
          borderBottomStyle: "solid",
          zIndex: 1000000,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Link href={feedTabRoutes[0]}>
              <StyledCompanyLogo
                src={CompanyLogo}
                alt="leap.club logo"
                width="44px"
                height="44px"
              />
            </Link>
            <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="primary"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={Boolean(anchorElNav)}
                onClose={() => setAnchorElNav(null)}
                sx={{
                  display: { xs: "block", md: "none" },
                }}
              >
                {pages.map(({ label, route }) => (
                  <MenuItem
                    key={label}
                    component={Link}
                    href={route}
                    onClick={() => handleCloseNavMenu(route, label)}
                  >
                    <Typography
                      textAlign="center"
                      color={
                        router.pathname.includes(route)
                          ? "primary"
                          : "secondary"
                      }
                    >
                      {label}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            <Box
              sx={{
                flexGrow: 1,
                display: { xs: "none", md: "flex" },
              }}
            >
              {pages.map(({ label, route }) => (
                <Button
                  key={label}
                  component={Link}
                  href={route}
                  variant="text"
                  onClick={() => handleCloseNavMenu(route, label)}
                  sx={{
                    ml: { md: 2 },
                  }}
                  color={getHeaderRouteColor(label, router, route)}
                  startIcon={getButtonIcon(route)}
                >
                  {label}
                </Button>
              ))}
            </Box>

            <Box sx={{ flexGrow: 0 }}>
              <Stack
                direction="row"
                justifyContent={"space-between"}
                alignItems="center"
              >
                {showBookmarkIcon && (
                  <IconButton
                    sx={{ mr: { sm: 0, xs: 0, md: 1.5, lg: 2 } }}
                    onClick={onBookmarkProfilePressHandler}
                  >
                    {isBookmarked ? (
                      <BookmarkIcon color="secondary" />
                    ) : (
                      <BookmarkBorderOutlinedIcon color="secondary" />
                    )}
                  </IconButton>
                )}

                <Link
                  href={`/search`}
                  sx={{ display: { xs: "flex", md: "flex", lg: "none" } }}
                >
                  <IconButton
                    sx={{ mr: 2 }}
                    onClick={() => {
                      trackEvent("search_bar_input_click", {
                        tab_name: getTabName(),
                        element_name: "search_bar",
                        element_type: analyticsConstants.TYPE.INPUT,
                        event_category: analyticsConstants.CATEGORY.CLICK,
                      });
                    }}
                    color={
                      router.pathname.includes("/search")
                        ? "primary"
                        : "secondary"
                    }
                  >
                    <SearchIcon />
                  </IconButton>
                </Link>

                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    px: showBookmarkIcon ? 1 : 2,
                    py: 0.5,
                    cursor: "pointer",
                    borderRadius: "12px",
                    mr: 2,
                    display: { xs: "none", md: "none", lg: "flex" },
                  }}
                  onClick={navigateToSearchMembersPage}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <IconButton
                      sx={{ pl: 0, ":hover": { backgroundColor: "#fff" } }}
                      disableTouchRipple
                      disableFocusRipple
                      onClick={navigateToSearchMembersPage}
                    >
                      <SearchIcon
                        fontSize="small"
                        sx={{ color: "rgba(0, 0, 0, 0.6)" }}
                      />
                    </IconButton>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="400"
                      fontSize={12}
                      ml={0.5}
                      onClick={navigateToSearchMembersPage}
                      // noWrap
                    >
                      search members or co.
                    </Typography>
                  </Stack>
                </Paper>

                <Link href={`/notifications`}>
                  <IconButton
                    color={
                      router.pathname.includes("/notifications")
                        ? "primary"
                        : "secondary"
                    }
                    onClick={() => {
                      trackEvent("notification_icon_click", {
                        tab_name: getTabName(),
                        element_name: "notification",
                        element_type: analyticsConstants.TYPE.ICON,
                        event_category: analyticsConstants.CATEGORY.CLICK,
                      });
                    }}
                  >
                    <Badge
                      badgeContent={
                        unreadInAppNotificationsCount?.unreadCount! > 99
                          ? "99+"
                          : unreadInAppNotificationsCount?.unreadCount
                      }
                      color="primary"
                    >
                      <NotificationsIcon />
                    </Badge>
                  </IconButton>
                </Link>
                <Link href={`/chat`}>
                  <IconButton
                    sx={{ mx: 2 }}
                    color={
                      router.pathname.includes("/chat")
                        ? "primary"
                        : "secondary"
                    }
                    onClick={() => {
                      trackEvent("chat_icon_click", {
                        tab_name: getTabName(),
                        element_name: "chat",
                        element_type: analyticsConstants.TYPE.ICON,
                        event_category: analyticsConstants.CATEGORY.CLICK,
                      });
                    }}
                  >
                    <Badge
                      badgeContent={
                        unreadChannelCount
                          ? unreadChannelCount > 10
                            ? "10+"
                            : unreadChannelCount
                          : 0
                      }
                      color="primary"
                    >
                      {/* <ChatIcon /> */}
                      {/* <MailOutlinedIcon /> */}
                      <MailIcon />
                    </Badge>
                  </IconButton>
                </Link>
                <IconButton
                  onClick={handleOpenUserMenu}
                  sx={{
                    p: 0,
                  }}
                >
                  <Avatar alt="user avatar" src={user?.userImage || ""}>
                    {user?.name?.charAt(0)}
                  </Avatar>
                </IconButton>
                {/* </Stack> */}
                <Menu
                  sx={{ mt: "45px" }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {settings.map(({ id, label }) => (
                    <MenuItem
                      key={id}
                      onClick={() => handleUserMenuItemClick(id, label)}
                    >
                      <Typography fontSize={12} textAlign="center">
                        {label}
                      </Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Stack>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <CustomDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <ShareProfile onClose={onClose} profileLink={profileLink} />
      </CustomDialog>
    </>
  );
};

export default MainHeader;
